// src/api/rigfrac.ts
//
// Rig/Frac Data Table API client — POST /api/v1/rigfrac/points against
// eva-data-svc-rs (EVA-66).
//
// The `/api/v1/rigfrac/values` dropdown-option source lives in a single hook,
// `useRigFracValuesQuery` (components/project/layers/queries), shared by the
// color-by adapter and the layer-filter popover so they hit one React Query
// cache entry — do NOT add a second `/values` query here.
//
// `id` MUST stay a JS string end-to-end (19-digit source id exceeds
// Number.MAX_SAFE_INTEGER) — never call Number()/parseInt on it.
import { useQuery } from "react-query";

import axios, { AxiosError } from "axios";
import { DATA_SVC_RS_ADDRESS } from "constants/app.constants";

// ── Rig/Frac Data Table (EVA-66) ─────────────────────────────────────────────

const RIGFRAC_ENDPOINT = `${DATA_SVC_RS_ADDRESS}/api/v1/rigfrac`;

// An empty array means "no constraint" to both backends, so this stands in for "nothing qualifies".
export const RIGFRAC_NO_MATCH_SENTINEL = "__EVA_NO_MATCH__";

export interface RigFracBbox {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

/** Keys mirror `RigFracValueSets` (`/values`) exactly — one key per filter control. */
export interface RigFracFilters {
  type?: string[];
  operator?: string[];
  country?: string[];
  stateProvince?: string[];
  county?: string[];
  basin?: string[];
  subBasin?: string[];
  locationType?: string[];
}

/** v1 sort.field is restricted to "activityDate" — the sole indexed sort column. */
export interface RigFracSort {
  field: "activityDate";
  dir: "asc" | "desc";
}

/** Exactly one of `bbox`/`polygon` is sent as the base region; `polygon` REPLACES `bbox` when present. */
export type RigFracSpatial =
  | {
      bbox: RigFracBbox;
      polygon?: undefined;
      excludePolygon?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
    }
  | {
      bbox?: undefined;
      polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon;
      excludePolygon?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
    };

export interface RigFracPointsRequestBody {
  bbox?: RigFracBbox;
  polygon?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  excludePolygon?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  historical: boolean;
  filters: RigFracFilters;
  // Optional per-layer selections, siblings of the flat `filters` fallback
  // above. Every field here is optional, so the narrower chart-svc filter
  // shape is structurally assignable without an adapter at the call sites.
  rigFilters?: RigFracFilters;
  fracFilters?: RigFracFilters;
  sort: RigFracSort;
  pageSize?: number;
  /** 1-based page number. */
  page?: number;
}

export interface RigFracPointRow {
  /** 19-digit source id. MUST stay a string — never parsed to a number. */
  id: string;
  activityType: "Rig" | "Frac" | "Unknown";
  /** ISO YYYY-MM-DD, week-ending Friday. */
  activityDate: string;
  /** Faithful 0..=9; 0 is legitimate and must never be coerced/dropped. */
  activityCount: number;
  operator: string;
  serviceCompany: string;
  rigNumber: string;
  basin: string;
  subBasin: string;
  stateProvince: string;
  countyUnique: string;
  country: string | null;
  locationType: string;
  /**
   * Rig trajectory as Novi reports it (EVA-107): "Horizontal" | "Directional" |
   * "Vertical" | "Other". Nullable and genuinely sparse — ~31% of rows have no
   * value (72,764 of 236,681 as of 2026-08-13), and frac crews have no rig to
   * describe — so the table renders "n/a" (see RIGFRAC_COLUMNS) rather than
   * treating null as an error.
   */
  rigTrajectory: string | null;
  lat: number;
  lng: number;
  isCurrentlyActive: boolean;
}

export interface RigFracPointsPage {
  rows: RigFracPointRow[];
  total: number;
  hasMore: boolean;
}

export function fetchRigFracPoints(
  body: RigFracPointsRequestBody
): Promise<RigFracPointsPage> {
  return axios
    .post<RigFracPointsPage>(`${RIGFRAC_ENDPOINT}/points`, body)
    .then((res) => res.data);
}

export interface UseRigFracPointsParams {
  /** null/undefined = no spatial input yet (e.g. viewport bounds not resolved) — query stays disabled. */
  spatial: Maybe<RigFracSpatial>;
  historical: boolean;
  filters: RigFracFilters;
  // See the matching comment on RigFracPointsRequestBody.
  rigFilters?: RigFracFilters;
  fracFilters?: RigFracFilters;
  sort: RigFracSort;
  pageSize?: number;
  /** 1-based page number. */
  page: number;
  /** Additional gate on top of `!!spatial` (e.g. widget not visible). */
  enabled?: boolean;
}

export interface UseRigFracPointsResult {
  rows: RigFracPointRow[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Paged-query hook over `POST /api/v1/rigfrac/points`. The query key includes
 * every field that changes the result set, so any change issues a fresh
 * request. `keepPreviousData` keeps the prior page's rows on screen while
 * the next page loads, instead of flashing empty.
 */
export function useRigFracPoints(params: UseRigFracPointsParams): UseRigFracPointsResult {
  const {
    spatial,
    historical,
    filters,
    rigFilters,
    fracFilters,
    sort,
    pageSize = 100,
    page,
    enabled = true
  } = params;

  const query = useQuery<RigFracPointsPage, AxiosError>(
    [
      "rigfrac-points",
      spatial,
      historical,
      filters,
      rigFilters,
      fracFilters,
      sort,
      pageSize,
      page
    ],
    () =>
      fetchRigFracPoints({
        ...(spatial ?? {}),
        historical,
        filters,
        rigFilters,
        fracFilters,
        sort,
        pageSize,
        page
      }),
    {
      enabled: enabled && !!spatial,
      keepPreviousData: true,
      retry: false
    }
  );

  return {
    rows: query.data?.rows ?? [],
    total: query.data?.total ?? 0,
    hasMore: query.data?.hasMore ?? false,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch
  };
}
